/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'

/**
 * UI state for the Master Route Productivity tab, shared between `Header`
 * (which hosts the panel-toggle + compare-route controls in its day-selector
 * row) and `ProductivityCompareView` (which renders the panels those controls
 * govern). The two are siblings under the same page, not parent/child, so
 * this can't be local `useState` on either side — a slice is the only shared
 * source of truth without prop-drilling through the page root.
 */
export const ROUTE_PRODUCTIVITY_KEY = 'routeProductivity' as const

export type WeekSpan = 1 | 2 | 4

interface RouteProductivityState {
  showList: boolean
  showMap: boolean
  routeBId: string | null
  // Start-of-week (Sunday) timestamp for the visible date window; null until
  // the view initializes it to the current week on mount. Stored as a number
  // (not a Date) to keep the slice serializable.
  weekStartMs: number | null
  // How many weeks the window spans (board-parity: 1 / 2 / 4).
  weekSpan: WeekSpan
  // Each column's day-chip filter, by the route it shows — the column reads it
  // straight back rather than keeping its own copy. It lives here because the
  // move menu offers days on BOTH routes and a column can't see its sibling's
  // state. Missing means unfiltered.
  dayKeysByRouteId: Record<string, ApiDayOfWeek[]>
}

const initialState: RouteProductivityState = {
  showList: true,
  showMap: true,
  routeBId: null,
  weekStartMs: null,
  weekSpan: 1,
  dayKeysByRouteId: {},
}

const routeProductivitySlice = createSlice({
  name: ROUTE_PRODUCTIVITY_KEY,
  initialState,
  reducers: {
    // At least one panel must stay visible: hiding one only takes effect while
    // the other is currently shown; a hidden panel always re-shows.
    toggleShowList: state => {
      if (!state.showList) {
        state.showList = true
        return
      }
      if (state.showMap) state.showList = false
    },
    toggleShowMap: state => {
      if (!state.showMap) {
        state.showMap = true
        return
      }
      if (state.showList) state.showMap = false
    },
    setRouteB: (state, action: PayloadAction<string>) => {
      state.routeBId = action.payload
    },
    clearRouteB: state => {
      state.routeBId = null
    },
    setWeekStart: (state, action: PayloadAction<number>) => {
      state.weekStartMs = action.payload
    },
    setWeekSpan: (state, action: PayloadAction<WeekSpan>) => {
      state.weekSpan = action.payload
    },
    setRouteDayKeys: (state, action: PayloadAction<{ routeId: string; keys: ApiDayOfWeek[] }>) => {
      state.dayKeysByRouteId[action.payload.routeId] = action.payload.keys
    },
    resetRouteProductivityState: () => initialState,
  },
})

export const {
  toggleShowList,
  toggleShowMap,
  setRouteB,
  clearRouteB,
  setWeekStart,
  setWeekSpan,
  setRouteDayKeys,
  resetRouteProductivityState,
} = routeProductivitySlice.actions

export interface RouteProductivityRootStateSlice {
  [ROUTE_PRODUCTIVITY_KEY]?: RouteProductivityState
}

const selectRouteProductivityState = (state: RouteProductivityRootStateSlice): RouteProductivityState =>
  state[ROUTE_PRODUCTIVITY_KEY] ?? initialState

export const selectShowList = (state: RouteProductivityRootStateSlice): boolean => selectRouteProductivityState(state).showList

export const selectShowMap = (state: RouteProductivityRootStateSlice): boolean => selectRouteProductivityState(state).showMap

export const selectRouteBId = (state: RouteProductivityRootStateSlice): string | null => selectRouteProductivityState(state).routeBId

export const selectWeekStartMs = (state: RouteProductivityRootStateSlice): number | null => selectRouteProductivityState(state).weekStartMs

export const selectWeekSpan = (state: RouteProductivityRootStateSlice): WeekSpan => selectRouteProductivityState(state).weekSpan

// Undefined when the column has never filtered — callers substitute the full
// week, which keeps the unfiltered default out of the store.
export const selectRouteDayKeys = (state: RouteProductivityRootStateSlice, routeId: string): ApiDayOfWeek[] | undefined =>
  selectRouteProductivityState(state).dayKeysByRouteId[routeId]

export { initialState as routeProductivityInitialState }
export type { RouteProductivityState }

export default routeProductivitySlice.reducer
